"use client"

import type { Session, User } from "@supabase/supabase-js"
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import { createClient } from "@/lib/supabase/browser"

/**
 * Client-side auth state — SINGLE source of truth.
 *
 * Exposes auth state for conditional UI (logged-out CTAs vs. app chrome).
 * This provider is NOT what protects data:
 *   - Data access is protected by RLS on `public.users` and every downstream
 *     user-owned table.
 *   - API routes are protected by `withAuth()` which re-validates the JWT on
 *     every request. Do not rely on any value from this provider for auth
 *     decisions on the server.
 *
 * Canonical import:
 *   import { AuthProvider, useAuth } from "@/providers/auth-provider"
 *
 * Back-compat: `useAuthContext` is a named alias exposing the same shape the
 * V1 marketing surface expected (`isLoggedIn`, `isLoading`, `profile`). New
 * code should prefer `useAuth` and destructure `user` / `loading` directly.
 */

/** Mirror of `public.users` row — kept minimal to avoid coupling. */
export type UserProfile = {
  id: string
  email: string | null
  full_name: string | null
  avatar_url: string | null
  plan: "free" | "premium" | "team"
  role: "user" | "admin"
}

type AuthContextValue = {
  user: User | null
  session: Session | null
  loading: boolean
  signOut: () => Promise<void>
  /** Derived: `user !== null`. Matches V1 marketing API. */
  isLoggedIn: boolean
  /** Alias for `loading`. Matches V1 marketing API. */
  isLoading: boolean
  /** `public.users` row for the current user. `null` while loading or logged out. */
  profile: UserProfile | null
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const supabase = useMemo(() => createClient(), [])
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState<UserProfile | null>(null)

  useEffect(() => {
    let mounted = true

    supabase.auth
      .getSession()
      .then(({ data: { session: initialSession } }) => {
        if (!mounted) return
        setSession(initialSession)
        setLoading(false)
      })
      .catch(() => {
        if (!mounted) return
        setLoading(false)
      })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return
      setSession(nextSession)
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [supabase])

  // Fetch the `public.users` row whenever the user changes.
  useEffect(() => {
    let mounted = true
    const userId = session?.user?.id

    if (!userId) {
      setProfile(null)
      return
    }

    supabase
      .from("users")
      .select("id, email, full_name, avatar_url, plan, role")
      .eq("id", userId)
      .single<UserProfile>()
      .then(({ data, error }) => {
        if (!mounted) return
        if (error) {
          // Row may not exist yet on first login — the `handle_new_user`
          // trigger populates it but there's a short race window. Swallow
          // the error; profile stays null and consumers can fall back on
          // `user.user_metadata`.
          setProfile(null)
          return
        }
        setProfile(data ?? null)
      })

    return () => {
      mounted = false
    }
  }, [supabase, session?.user?.id])

  const value = useMemo<AuthContextValue>(
    () => {
      const user = session?.user ?? null
      return {
        user,
        session,
        loading,
        isLoggedIn: user !== null,
        isLoading: loading,
        profile,
        signOut: async () => {
          await supabase.auth.signOut()
        },
      }
    },
    [session, loading, profile, supabase],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>")
  }
  return ctx
}

/**
 * Back-compat alias for V1 marketing components. Prefer `useAuth` in new code.
 */
export const useAuthContext = useAuth
